import React, { useEffect, useState } from "react";
import { X, AlertCircle, Info, CheckCircle, Bell } from "lucide-react";
import { Button } from "./Button";

interface PopupNotificationProps {
  id: string;
  title: string;
  message: string;
  priority?: {
    id: number;
    name: string;
  };
  type?: {
    id: number;
    name: string;
  };
  url?: string | null;
  expiryTime?: string | null;
  onClose: (id: string) => void;
  onAction?: () => void;
}

const PopupNotification: React.FC<PopupNotificationProps> = ({
  id,
  title,
  message,
  priority,
  type,
  url,
  expiryTime,
  onClose,
  onAction,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);
  
  // Note: This component is for Popup and In-App type notifications only
  // For Announcement and Marketing types, use AnnouncementNotificationDialog

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300);
  };

  const handleAction = () => {
    if (url) {
      window.open(url, "_blank");
    }
    if (onAction) {
      onAction();
    }
    handleClose();
  };

  const getPriorityIcon = () => {
    const priorityName = priority?.name?.toLowerCase();
    const priorityId = priority?.id;
    
    // Check by name first, then by ID
    if (priorityName === "high" || priorityName === "urgent" || priorityId === 3) {
      return <AlertCircle className="h-6 w-6 text-red-500" />;
    } else if (priorityName === "low" || priorityId === 1) {
      return <Info className="h-6 w-6 text-blue-500" />;
    } else {
      return <Bell className="h-6 w-6 text-gray-500" />;
    }
  };

  const getPriorityColor = () => {
    const priorityName = priority?.name?.toLowerCase();
    const priorityId = priority?.id;
    
    // Check by name first, then by ID
    if (priorityName === "high" || priorityName === "urgent" || priorityId === 3) {
      return "border-red-500 bg-red-50";
    } else if (priorityName === "low" || priorityId === 1) {
      return "border-blue-500 bg-blue-50";
    } else {
      return "border-gray-300 bg-white";
    }
  };
  
  const getPriorityLabel = () => {
    const priorityName = priority?.name;
    const priorityId = priority?.id;
    
    // Return name if available
    if (priorityName) {
      return priorityName;
    }
    
    // Otherwise map from ID
    switch (priorityId) {
      case 1:
        return "Low";
      case 2:
        return "Medium";
      case 3:
        return "High";
      case 4:
        return "Normal";
      default:
        return "Normal";
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{ 
          zIndex: 9998,
          pointerEvents: "auto"
        }}
        onClick={handleClose}
      />

      {/* Popup Modal */}
      <div
        className={`fixed inset-0 flex items-center justify-center p-4 transition-all duration-300 ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
        style={{ 
          zIndex: 9999,
          pointerEvents: "none"
        }}
      >
        <div
          className={`bg-white rounded-xl shadow-2xl max-w-md w-full border-l-4 ${getPriorityColor()} transform transition-all`}
          style={{ 
            textAlign: "left",
            pointerEvents: "auto"
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start gap-3 p-6 border-b">
            <div className="flex-shrink-0 mt-0.5">{getPriorityIcon()}</div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 break-words">
                {title}
              </h3>
              {priority && (
                <span
                  className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                    priority.id === 3
                      ? "bg-red-100 text-red-700"
                      : priority.id === 1
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {getPriorityLabel()} Priority
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {message}
            </p>

            {expiryTime && (
              <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>⏰ Expires:</strong>{" "}
                  {new Date(expiryTime).toLocaleString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-6 border-t bg-gray-50">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClose}
            >
              Close
            </Button>
            {url && (
              <Button
                className="flex-1"
                onClick={handleAction}
              >
                View Details
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PopupNotification;

